import React, { useState, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Button, Slider, Select, Input, Spin, Empty, Pagination, Tag, Drawer, message,
} from 'antd';
import { FilterOutlined, LoadingOutlined, SearchOutlined, CrownOutlined, CloseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { State } from 'country-state-city';
import ISO6391 from 'iso-639-1';
import CustomNavbar from '../components/Navbar';
import SearchCard from '../components/SearchCard';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';

import religionData from '../data/religion.json';
import denominationData from '../data/denomination.json';
import casteData from '../data/caste.json';

const { Option } = Select;

/* ── static option lists (values must match DB ENUMs exactly) ────── */
const MARITAL_STATUS  = ['Single', 'Divorced', 'Widowed'];
const RELIGIONS       = religionData.religions.map(r => r.name);
const DENOMINATIONS   = denominationData.denominations.map(d => d.name);
const CASTES          = casteData.castes.map(c => c.name);
const MOTHER_TONGUES  = ISO6391.getAllCodes().map(code => ISO6391.getName(code)).filter(Boolean).sort();
const EDUCATIONS      = [
    'Below 10th', '10th Pass / SSLC', '12th Pass / HSC', 'Diploma / Polytechnic',
    "Graduate / Bachelor's", "Postgraduate / Master's", 'Doctorate / PhD',
    'Professional Degree', 'Other',
];
const OCCUPATIONS     = [
    'Engineer', 'Doctor', 'Teacher', 'Business', 'Self-employed', 'Lawyer',
    'Architect', 'Scientist', 'Artist', 'Researcher', 'Writer', 'CA/Accountant',
    'IT Professional', 'Other',
];
const EMPLOYED_IN     = [
    'Government', 'Private', 'Business', 'Self-employed', 'Defence/Civil Services',
    'NGO/Social Sector', 'Student', 'Homemaker', 'Retired', 'Not working', 'Other',
];
const ANNUAL_INCOME   = [
    '0-5 Lakhs', '5-10 Lakhs', '10-15 Lakhs', '15-20 Lakhs', '20-25 Lakhs',
    '25-30 Lakhs', '30-40 Lakhs', '40-50 Lakhs', '50 Lakhs - 1 Crore',
    '1-2 Crore', '2-5 Crore', '5-10 Crore', '10-20 Crore', '20-50 Crore',
    '50-100 Crore', '100+ Crore', 'No Income', 'Prefer not to say',
];
const BODY_TYPES      = ['Slim', 'Athletic', 'Average', 'Heavy'];
const COMPLEXIONS     = ['Fair', 'Medium', 'Dark'];
const PHYSICAL_STATUSES = ['Normal', 'Physically Challenged'];
const DIETS           = ['Veg', 'Non-Veg', 'Eggetarian', 'Vegan'];
const INDIAN_STATES   = State.getStatesOfCountry('IN').map(s => s.name);
const FAMILY_TYPES    = ['Joint', 'Nuclear'];
const FAMILY_VALUES   = ['Traditional', 'Moderate', 'Liberal'];

/* ── height helpers ──────────────────────────────────────────────── */
const ftInchToDecimal = (ft, inch) => ft + inch / 12;
const decimalToLabel  = (val) => {
    const ft   = Math.floor(val);
    const inch = Math.round((val - ft) * 12);
    return `${ft}'${inch}"`;
};
const HEIGHT_MIN = ftInchToDecimal(4, 0);
const HEIGHT_MAX = ftInchToDecimal(7, 0);

/* ── filter section ──────────────────────────────────────────────── */
const FilterSection = ({ label, children }) => (
    <div style={{ marginBottom: '18px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
            {label}
        </div>
        {children}
    </div>
);

const MS = { width: '100%', fontSize: '0.85rem' };

const BLANK_FILTERS = {
    age_min: 18, age_max: 60,
    height_min: null, height_max: null,
    marital_status: [], religion: [], denomination: [],
    caste: [], mother_tongue: [], education: [], occupation: [],
    employed_in: [], annual_income: [], body_type: [], complexion: [],
    physical_status: [], diet: [], smoke: null, drink: null,
    city: '', state: [], family_type: [], family_values: [],
};

/* ── main component ──────────────────────────────────────────────── */
const AdvancedSearch = () => {
    const { profileId: loggedInProfileId } = useProfile();
    const navigate = useNavigate();

    const [isMobile, setIsMobile]           = useState(window.innerWidth < 768);
    const [drawerOpen, setDrawerOpen]       = useState(false);
    const [filters, setFilters]             = useState(BLANK_FILTERS);
    const [results, setResults]             = useState([]);
    const [pagination, setPagination]       = useState({ total: 0, page: 1, perPage: 12, lastPage: 1 });
    const [isLoading, setIsLoading]         = useState(false);
    const [hasSearched, setHasSearched]     = useState(false);
    const [upgradeRequired, setUpgradeRequired] = useState(false);
    const [planChecking, setPlanChecking]   = useState(true);

    const ALLOWED_PLANS = ['premium', 'elite', 'ultimate', 'exclusive'];

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        api.get('/subscriptions/status').then(res => {
            const planId = res.data?.data?.subscription?.subscription_plan?.plan_id;
            if (!planId || !ALLOWED_PLANS.includes(planId)) setUpgradeRequired(true);
        }).catch(() => setUpgradeRequired(true))
          .finally(() => setPlanChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sf = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

    const activeFilterCount = (() => {
        let n = 0;
        if (filters.age_min !== 18 || filters.age_max !== 60) n++;
        if (filters.height_min || filters.height_max) n++;
        ['marital_status','religion','denomination','caste','mother_tongue','education',
         'occupation','employed_in','annual_income','body_type','complexion',
         'physical_status','diet','state','family_type','family_values']
            .forEach(k => { if (filters[k]?.length) n++; });
        if (filters.smoke) n++;
        if (filters.drink) n++;
        if (filters.city?.trim()) n++;
        return n;
    })();

    const buildPayload = (page = 1) => {
        const payload = { profile_id: loggedInProfileId, page, limit: pagination.perPage };
        const f = filters;
        if (f.age_min !== 18 || f.age_max !== 60) { payload.age_min = f.age_min; payload.age_max = f.age_max; }
        if (f.height_min !== null) payload.height_min = f.height_min;
        if (f.height_max !== null) payload.height_max = f.height_max;
        ['marital_status','religion','denomination','caste','mother_tongue','education',
         'occupation','employed_in','annual_income','body_type','complexion',
         'physical_status','diet','state','family_type','family_values']
            .forEach(k => { if (f[k]?.length) payload[k] = f[k]; });
        if (f.smoke) payload.smoke = f.smoke;
        if (f.drink) payload.drink = f.drink;
        if (f.city?.trim()) payload.city = f.city.trim();
        return payload;
    };

    const doSearch = useCallback(async (page = 1) => {
        setIsLoading(true);
        setHasSearched(true);
        setUpgradeRequired(false);
        try {
            const res = await api.post('/profiles/search/advanced', buildPayload(page));
            if (res.data.status === 'success') {
                const d = res.data.data;
                setResults(d.profiles);
                setPagination({ total: d.total, page: d.current_page, perPage: d.per_page, lastPage: d.last_page });
            } else {
                message.error(res.data.message || 'Search failed.');
            }
        } catch (err) {
            if (err.response?.data?.upgrade_required) {
                setUpgradeRequired(true);
            } else {
                message.error('Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, loggedInProfileId]);

    const handleSearch = () => {
        setDrawerOpen(false);
        doSearch(1);
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, page }));
        doSearch(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /* ── filter panel content (shared between sidebar & drawer) ─── */
    const filterPanel = (
        <div>
            <FilterSection label="Age">
                <Slider
                    range min={18} max={70}
                    value={[filters.age_min, filters.age_max]}
                    onChange={([min, max]) => { sf('age_min', min); sf('age_max', max); }}
                    tooltip={{ formatter: v => `${v} yrs` }}
                    trackStyle={[{ backgroundColor: '#AD1457' }]}
                    handleStyle={[{ borderColor: '#AD1457' }, { borderColor: '#AD1457' }]}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                    <span>{filters.age_min} yrs</span><span>{filters.age_max} yrs</span>
                </div>
            </FilterSection>

            <FilterSection label="Height">
                <Slider
                    range min={HEIGHT_MIN} max={HEIGHT_MAX} step={1 / 12}
                    value={[filters.height_min ?? HEIGHT_MIN, filters.height_max ?? HEIGHT_MAX]}
                    onChange={([min, max]) => {
                        sf('height_min', Math.round(min * 100) / 100);
                        sf('height_max', Math.round(max * 100) / 100);
                    }}
                    tooltip={{ formatter: v => decimalToLabel(v) }}
                    trackStyle={[{ backgroundColor: '#AD1457' }]}
                    handleStyle={[{ borderColor: '#AD1457' }, { borderColor: '#AD1457' }]}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--color-text-secondary)', marginTop: '2px' }}>
                    <span>{decimalToLabel(filters.height_min ?? HEIGHT_MIN)}</span>
                    <span>{decimalToLabel(filters.height_max ?? HEIGHT_MAX)}</span>
                </div>
            </FilterSection>

            <FilterSection label="Marital Status">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.marital_status} onChange={v => sf('marital_status', v)}>
                    {MARITAL_STATUS.map(s => <Option key={s}>{s}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Religion">
                <Select mode="multiple" allowClear showSearch placeholder="Any" style={MS} value={filters.religion} onChange={v => sf('religion', v)}>
                    {RELIGIONS.map(r => <Option key={r}>{r}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Denomination">
                <Select mode="multiple" allowClear showSearch placeholder="Any" style={MS} value={filters.denomination} onChange={v => sf('denomination', v)}>
                    {DENOMINATIONS.map(d => <Option key={d}>{d}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Caste">
                <Select mode="multiple" allowClear showSearch placeholder="Any" style={MS} value={filters.caste} onChange={v => sf('caste', v)}>
                    {CASTES.map(c => <Option key={c}>{c}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Mother Tongue">
                <Select mode="multiple" allowClear showSearch placeholder="Any" style={MS} value={filters.mother_tongue} onChange={v => sf('mother_tongue', v)}>
                    {MOTHER_TONGUES.map(m => <Option key={m}>{m}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Highest Education">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.education} onChange={v => sf('education', v)}>
                    {EDUCATIONS.map(e => <Option key={e}>{e}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Occupation">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.occupation} onChange={v => sf('occupation', v)}>
                    {OCCUPATIONS.map(o => <Option key={o}>{o}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Employed In">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.employed_in} onChange={v => sf('employed_in', v)}>
                    {EMPLOYED_IN.map(e => <Option key={e}>{e}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Annual Income">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.annual_income} onChange={v => sf('annual_income', v)}>
                    {ANNUAL_INCOME.map(a => <Option key={a}>{a}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Body Type">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.body_type} onChange={v => sf('body_type', v)}>
                    {BODY_TYPES.map(b => <Option key={b}>{b}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Complexion">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.complexion} onChange={v => sf('complexion', v)}>
                    {COMPLEXIONS.map(c => <Option key={c}>{c}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Physical Status">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.physical_status} onChange={v => sf('physical_status', v)}>
                    {PHYSICAL_STATUSES.map(p => <Option key={p}>{p}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Diet">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.diet} onChange={v => sf('diet', v)}>
                    {DIETS.map(d => <Option key={d}>{d}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Smoking">
                <Select allowClear placeholder="Any" style={MS} value={filters.smoke} onChange={v => sf('smoke', v)}>
                    <Option value="Yes">Smoker</Option>
                    <Option value="No">Non-Smoker</Option>
                </Select>
            </FilterSection>

            <FilterSection label="Drinking">
                <Select allowClear placeholder="Any" style={MS} value={filters.drink} onChange={v => sf('drink', v)}>
                    <Option value="Yes">Drinker</Option>
                    <Option value="No">Non-Drinker</Option>
                </Select>
            </FilterSection>

            <FilterSection label="City">
                <Input
                    placeholder="Enter city name"
                    value={filters.city}
                    onChange={e => sf('city', e.target.value)}
                    allowClear
                    style={{ borderRadius: '8px', fontSize: '0.85rem' }}
                />
            </FilterSection>

            <FilterSection label="State">
                <Select mode="multiple" allowClear showSearch placeholder="Any" style={MS} value={filters.state} onChange={v => sf('state', v)}>
                    {INDIAN_STATES.map(s => <Option key={s}>{s}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Family Type">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.family_type} onChange={v => sf('family_type', v)}>
                    {FAMILY_TYPES.map(f => <Option key={f}>{f}</Option>)}
                </Select>
            </FilterSection>

            <FilterSection label="Family Values">
                <Select mode="multiple" allowClear placeholder="Any" style={MS} value={filters.family_values} onChange={v => sf('family_values', v)}>
                    {FAMILY_VALUES.map(f => <Option key={f}>{f}</Option>)}
                </Select>
            </FilterSection>

            <Button
                type="primary" block icon={<SearchOutlined />}
                onClick={handleSearch} loading={isLoading}
                style={{ marginTop: '8px', borderRadius: '10px', fontWeight: 600, height: '42px', background: '#AD1457', borderColor: '#AD1457' }}
            >
                Apply Filters
            </Button>
        </div>
    );

    /* ── loading spinner ─────────────────────────────────────────── */
    if (planChecking) {
        return (
            <div style={{ fontFamily: 'Calibri, sans-serif', height: '100vh', overflowY: 'auto', background: 'var(--color-bg)' }}>
                <Helmet><title>Advanced Search | Oottan</title></Helmet>
                <CustomNavbar />
                <div style={{ marginTop: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#AD1457' }} spin />} />
                </div>
            </div>
        );
    }

    /* ── upgrade gate ────────────────────────────────────────────── */
    if (upgradeRequired) {
        return (
            <div style={{ fontFamily: 'Calibri, sans-serif', height: '100vh', overflowY: 'auto', background: 'var(--color-bg)' }}>
                <Helmet><title>Advanced Search | Oottan</title></Helmet>
                <CustomNavbar />
                <div style={{ marginTop: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', padding: '40px 16px', textAlign: 'center' }}>
                    <div style={{ background: 'white', borderRadius: '20px', padding: '48px 32px', maxWidth: '480px', width: '100%', border: '1px solid rgba(173,20,87,0.1)', boxShadow: '0 8px 40px rgba(173,20,87,0.08)' }}>
                        <CrownOutlined style={{ fontSize: '48px', color: '#AD1457', marginBottom: '16px' }} />
                        <h2 style={{ fontWeight: 700, fontSize: '1.4rem', color: 'var(--color-text-primary)', marginBottom: '12px' }}>
                            Premium Feature
                        </h2>
                        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem', marginBottom: '28px', lineHeight: 1.6 }}>
                            Advanced Search is available for Premium, Elite, Ultimate, and Exclusive members. Upgrade your plan to access detailed filters and find your perfect match.
                        </p>
                        <Button
                            type="primary" size="large" icon={<CrownOutlined />}
                            onClick={() => navigate('/pricing')}
                            style={{ borderRadius: '10px', fontWeight: 600, background: '#AD1457', borderColor: '#AD1457', height: '46px', paddingInline: '32px' }}
                        >
                            View Plans
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    /* ── main layout ─────────────────────────────────────────────── */
    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', minHeight: '100vh', background: 'var(--color-bg)' }}>
            <Helmet><title>Advanced Search | Oottan</title></Helmet>
            <CustomNavbar />

            <div style={{ marginTop: '64px', maxWidth: '1200px', margin: '64px auto 0', padding: isMobile ? '20px 12px 60px' : '32px 16px 60px' }}>

                {/* Header */}
                <div style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'var(--color-primary-faint)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FilterOutlined style={{ fontSize: '16px', color: 'var(--color-primary)' }} />
                            </div>
                            <div>
                                <h1 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem', fontWeight: 700, color: 'var(--color-text-primary)', margin: 0 }}>
                                    Advanced Search
                                </h1>
                                {!isMobile && (
                                    <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.88rem', margin: 0 }}>
                                        Use detailed filters to find your ideal match
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Mobile: filter button */}
                        {isMobile && (
                            <Button
                                onClick={() => setDrawerOpen(true)}
                                icon={<FilterOutlined />}
                                style={{ borderRadius: '10px', borderColor: '#AD1457', color: '#AD1457', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}
                            >
                                Filters
                                {activeFilterCount > 0 && (
                                    <Tag color="#AD1457" style={{ marginLeft: '4px', borderRadius: '8px', fontSize: '0.7rem', lineHeight: '1.4', padding: '0 5px' }}>
                                        {activeFilterCount}
                                    </Tag>
                                )}
                            </Button>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

                    {/* Desktop sidebar */}
                    {!isMobile && (
                        <div style={{
                            width: '272px', flexShrink: 0,
                            background: 'white', borderRadius: '16px',
                            border: '1px solid rgba(173,20,87,0.08)',
                            padding: '18px',
                            height: 'fit-content',
                            position: 'sticky', top: '80px',
                            maxHeight: 'calc(100vh - 100px)',
                            overflowY: 'auto',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text-primary)' }}>
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <Tag color="#AD1457" style={{ marginLeft: '6px', borderRadius: '10px', fontSize: '0.68rem' }}>
                                            {activeFilterCount}
                                        </Tag>
                                    )}
                                </span>
                                {activeFilterCount > 0 && (
                                    <span onClick={() => setFilters(BLANK_FILTERS)} style={{ fontSize: '0.75rem', color: '#AD1457', cursor: 'pointer', fontWeight: 500 }}>
                                        Clear all
                                    </span>
                                )}
                            </div>
                            {filterPanel}
                        </div>
                    )}

                    {/* Mobile: filter drawer */}
                    {isMobile && (
                        <Drawer
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                                        Filters
                                        {activeFilterCount > 0 && (
                                            <Tag color="#AD1457" style={{ marginLeft: '8px', borderRadius: '10px', fontSize: '0.7rem' }}>
                                                {activeFilterCount}
                                            </Tag>
                                        )}
                                    </span>
                                    {activeFilterCount > 0 && (
                                        <span onClick={() => setFilters(BLANK_FILTERS)} style={{ fontSize: '0.8rem', color: '#AD1457', cursor: 'pointer', fontWeight: 500 }}>
                                            Clear all
                                        </span>
                                    )}
                                </div>
                            }
                            placement="left"
                            open={drawerOpen}
                            onClose={() => setDrawerOpen(false)}
                            closeIcon={<CloseOutlined />}
                            width={Math.min(320, window.innerWidth - 32)}
                            styles={{ body: { padding: '16px' } }}
                        >
                            {filterPanel}
                        </Drawer>
                    )}

                    {/* Results */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        {isLoading && (
                            <div style={{ textAlign: 'center', padding: '80px 0' }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 40, color: '#AD1457' }} spin />} />
                                <p style={{ marginTop: '16px', color: 'var(--color-text-secondary)' }}>Searching profiles...</p>
                            </div>
                        )}

                        {!isLoading && hasSearched && results.length === 0 && (
                            <div style={{ textAlign: 'center', padding: '80px 0' }}>
                                <Empty description={<span style={{ color: 'var(--color-text-secondary)' }}>No profiles found matching your filters.</span>} />
                            </div>
                        )}

                        {!isLoading && !hasSearched && (
                            <div style={{ textAlign: 'center', padding: '80px 0' }}>
                                <FilterOutlined style={{ fontSize: '48px', color: 'rgba(173,20,87,0.2)', marginBottom: '16px', display: 'block' }} />
                                <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem' }}>
                                    {isMobile
                                        ? <>Tap <strong>Filters</strong> to set your criteria and search.</>
                                        : <>Set your filters and click <strong>Apply Filters</strong> to search.</>
                                    }
                                </p>
                            </div>
                        )}

                        {!isLoading && results.length > 0 && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.88rem' }}>
                                        {pagination.total} profile{pagination.total !== 1 ? 's' : ''} found
                                    </span>
                                </div>

                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: isMobile
                                        ? 'repeat(auto-fill, minmax(160px, 1fr))'
                                        : 'repeat(auto-fill, minmax(220px, 1fr))',
                                    gap: isMobile ? '12px' : '20px',
                                }}>
                                    {results.map(profile => (
                                        <SearchCard key={profile.profile_id} profile={profile} />
                                    ))}
                                </div>

                                {pagination.lastPage > 1 && (
                                    <div style={{ textAlign: 'center', marginTop: '36px' }}>
                                        <Pagination
                                            current={pagination.page}
                                            total={pagination.total}
                                            pageSize={pagination.perPage}
                                            onChange={handlePageChange}
                                            showSizeChanger={false}
                                            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                                            size={isMobile ? 'small' : 'default'}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdvancedSearch;
